import { Inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";

import { ResourcePackageToken, ResourcePackage, ResourceMap } from "../model/resource.model";
import { ResourceService } from "./resource.service";
import { LocaleService, LocaleServiceToken } from "../../../../core/locale/locale.module";
import { Observable } from "rxjs";
import { ResourcesState } from "../../../../core/redux/redux.module";
import { selectRecourcesState } from "../../../../core/redux/model/default/resources.state.model";
import { selectCoreState } from "../../../../core/redux/model/common/core.state.model";

/**
 * The default implemetation of the resource service. That collects the resource packages from Angular multi providers, based on the {@link ResourcePackageToken}.
 */
@Injectable()
export class ResourceServiceImpl implements ResourceService {
    protected _localeService: LocaleService;
    protected _resources: ResourcePackage;
    protected _localeResources: ResourceMap;
    protected _frontendResources: ResourcePackage[];

    private ngrxStore: Store;

    constructor (
        @Inject(LocaleServiceToken) localeService: LocaleService,
        @Inject(ResourcePackageToken) resources: ResourcePackage[],
        ngrxStore: Store<any>
    ) {
        this.ngrxStore = ngrxStore;
        this._localeService = localeService;

        this._frontendResources = resources;
        this.processResources(resources);

        ngrxStore.select(selectCoreState).subscribe(this.applyBackendResources.bind(this));
    }

    /**
     * This method resolves a resource key and replaces it a with a locale-aware text item. You can use parametrized
     * texts with named placeholders to support context-aware contents.
     * @param key The resource key to resolve.
     * @param parameters A parameter object for replacing the mustache style, named placeholders in the resolved text.
     * @param defaultValue If there is no value registered to the given resource key, this default value will be return as the result of the resolve.
     */
    public resolve(key: string, parameters?: any, defaultValue?: string): string {

        this.applyLocale();

        let template = "";
        if (typeof parameters === "string") {
            defaultValue = parameters;
        }

        if (key.indexOf(".null") > 0 || key.indexOf(".undefined") > 0) {
            return "";
        }

        if (this.hasKey(key)) {
            template = this._localeResources[key];
        } else {
            if (!defaultValue && defaultValue !== "") {
                return "Undefined resource: [" + key + "]";
            }

            template = defaultValue;
        }

        if (parameters) {
            return this.deMustache(template, parameters);
        }

        return template;
    }

    /**
     * Checks whether the given resource kay is registered for the actual locale.
     * @param key The target resource key.
     */
    public hasKey(key: string): boolean {
        return this._localeResources[key] !== undefined;
    }

    // merging the provided resource packages
    protected processResources(packages: ResourcePackage[]) {
        this._resources = {};

        for (const currentPackage of packages) {
            for (const locale in currentPackage) {
              if (currentPackage.hasOwnProperty(locale)) {
                if (!this._resources[locale]) {
                  this._resources[locale] = {};
                }
                Object.assign(this._resources[locale], currentPackage[locale]);
              }
            }
        }
    }

    protected applyLocale() {
        const locale: string = this._localeService.getLocale();

        if (!this._resources[locale]) {
            throw new Error("This locale (" + locale + ") is not defined in any of our resource packages!");
        }

        this._localeResources = this._resources[locale];
    }

    protected applyBackendResources(): void {
        this.processResources(this._frontendResources);
        this.applyLocale();

        const locale: string = this._localeService.getLocale();
        const resourcesState: Observable<ResourcesState> = this.ngrxStore.select(selectRecourcesState);
        resourcesState.subscribe( state => {
          const resourceList = state[locale];
          if (!resourceList) {
            return;
          }
          Object.assign(this._localeResources, resourceList);
        });
    }

    // resolving mustache style named placeholders to support context-aware elements
    private deMustache(template: string, parameters: any) {
        return template.replace(/{{([^}]*)}}/g, (all, param: string) => {
            return parameters[param] || all;
        });
    }
}
